import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {HttpLoginRequest} from 'app/models/http-login-request';
import {RedirectBackURL} from 'app/models/redirect-back-url';
import {VerificationResult} from 'app/models/verification-result.enum';
import {WebUserLoginAttemptInfo} from 'app/models/webuser-login-attempt-Info';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {SummitUtilService} from '@summit/utils';

@Injectable()
export class AuthenticationService {
  private _request: HttpLoginRequest;
  private _jpassword: string;
  private _autoLaunchChatbot: string;


  constructor(
    private http: HttpClient,
    private summitUtils: SummitUtilService) {
  }

  public get request(): HttpLoginRequest {
    return this._request;
  }

  public set request(value: HttpLoginRequest) {
    this._request = value;
  }

  public get jpassword(): string {
    return this._jpassword;
  }

  public set jpassword(value: string) {
    this._jpassword = value;
  }

  get autoLaunchChatbot(): string {
    return this._autoLaunchChatbot;
  }

  set autoLaunchChatbot(value: string) {
    this._autoLaunchChatbot = value;
  }

  public verify(): Observable<VerificationResult> {
    const headers = {'Authorization': 'Basic ' + window.btoa(this._jpassword)};
    const url: string = environment.authUrl + 'verifyLogin';
    return this.http.post<VerificationResult>(url, this.request, {headers: headers, withCredentials: true})
      .pipe(
        catchError(err => {
          console.error('failed to verify credentials.', err);
          const message = 'Something went wrong during authentication process ';
          return this.summitUtils.errorHandlerImpl(err, message, environment.production);
        })
      );
  }

  public verifyOneTimePasscodeEnteredIsValid(oneTimePasscodeInput: string): Observable<VerificationResult> {
    this.request.securityCode = oneTimePasscodeInput;
    const url: string = environment.authUrl + 'verifySecurityCode';
    return this.http.post<VerificationResult>(url, this.request)
      .pipe(
        catchError(err => {
          console.error('failed to verify credentials.', err);
          const message = 'Something went wrong during authentication process ';
          return this.summitUtils.errorHandlerImpl(err, message, environment.production);
        })
      );
  }

  public resendCode(): Observable<VerificationResult> {
    const url: string = environment.authUrl + 'resendSecurityCode';
    return this.http.post<VerificationResult>(url, this.request.userLogin)
      .pipe(
        catchError(err => {
          console.error('failed to verify credentials.', err);
          const message = 'Something went wrong during authentication process ';
          return this.summitUtils.errorHandlerImpl(err, message, environment.production);
        })
      );
  }

  public userLogin(username: string, password: string): Observable<RedirectBackURL> {
    const headers = {'Authorization': 'Basic ' + window.btoa(password)};
    const url: string = environment.authUrl + 'userLogin';
    return this.http.post<RedirectBackURL>(url, username, {headers: headers})
      .pipe(
        catchError(err => {
          console.error('failed to retrieve the RedirectBackURL.', err);
          const message = 'Something went wrong when trying during authentication process ';
          return this.summitUtils.errorHandlerImpl(err, message, environment.production);
        })
      );
  }

  public getLoginAttempt(): Observable<WebUserLoginAttemptInfo> {
    const urlStr: string = environment.authUrl + 'loginAttempt?userLogin=' + this.request.userLogin;
    return this.http.get<WebUserLoginAttemptInfo>(urlStr)
      .pipe(
        catchError(err => {
          console.error('failed to retrieve the WebUserLoginAttemptInfo.', err);
          const message = 'Something went wrong when trying during authentication process ';
          return this.summitUtils.errorHandlerImpl(err, message, environment.production);
        })
      );
  }

  public isWebsiteAccessSuspended(userLogin: string): Observable<boolean> {
    const urlStr: string = environment.authUrl + 'isWebsiteAccessSuspended?userLogin=' + userLogin;
    return this.http.get<boolean>(urlStr)
      .pipe(
        catchError(err => {
          console.error('failed to retrieve the account websiteAccessSuspended flag.', err);
          const message = 'Something went wrong when trying during authentication process ';
          return this.summitUtils.errorHandlerImpl(err, message, environment.production);
        })
      );
  }

  public hasActiveAccounts(userLogin: string): Observable<boolean> {
    const url = `${environment.authUrl}loginHasActiveAccounts?userLogin=${userLogin}`;
    return this.http.get<boolean>(url)
      .pipe(
        catchError(err => {
          const message = 'There was a problem checking for active accounts.';
          const error = this.summitUtils.errorHandlerImpl(err, message, environment.production);
          return error;
        })
      );
  }

  public generateOneTimePasscodeEmail(): Observable<boolean> {
    const urlStr: string = environment.authUrl + 'securityCode';
    return this.http.post<boolean>(urlStr, this.request.userLogin)
      .pipe(
        catchError(err => {
          const message = 'Something went wrong when generating a one time passcode';
          return this.summitUtils.errorHandlerImpl(err, message, environment.production);
        })
      );
  }

  public isLoggedIn(): Observable<boolean> {
    return this.http.get<boolean>(environment.restfulServices + 'IsLoggedIn')
      .pipe(
        catchError(error => {
          return this.summitUtils.errorHandlerImpl(error, 'Failed to check it user is logged in.', environment.production);
        })
      );
  }

  public getCredentials(): Observable<any> {
    const url = environment.restfulServices + 'secured/json/credentials';
    return this.http.get(url)
      .pipe(
        catchError(error => {
          return this.summitUtils.errorHandlerImpl(error, 'Failed to get user credentials', environment.production);
        })
      );
  }

  public setChatRequirements() {
    if (this.autoLaunchChatbot) {
      localStorage.setItem('chatSource', this.autoLaunchChatbot);
    }
  }
}
